import {
  SiteInfo,
  LoginProvider,
  UserProperties,
  GeneralSettingsSnapshot,
  Campaign,
  InvitedSpaceIdAndRoomId,
} from './../../utils/models';
import Vue from 'vue';
import Vuex from 'vuex';
import {
  ImageWithDimensions,
  SettingsSnapshot,
  User,
  UserRoomState,
  NotificationModes,
  CustomModes,
  SiteStatus,
} from '@/utils/models';
import { isIPad } from '@/utils/functions';
import { DataService } from '@/service/DataService';
import store, { set } from 'store';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    isIPad: isIPad(),
    roomScale: 1 as number,
    isVideoAvailable: true as boolean,
    isAudioAvailable: true as boolean,
    uploadedImageBase64: null as ImageWithDimensions | null,
    siteInfo: null as SiteInfo | null,
    isScreenSharing: false as boolean,
    dataService: (<any>null) as DataService,
    videoDeviceId: store.get('chosenVideoInputDeviceId') as string,
    /** mic */
    audioInputDeviceId: store.get('chosenAudioInputDeviceId') as string,
    /** speaker */
    audioOutputDeviceId: store.get('chosenAudioOutputDeviceId') as string,
    isAudioEnabledOnInit: false,
    isVideoEnabledOnInit: false,
    user: {} as User | null,
    userProperties: {} as UserProperties | null,
    hoverUser: null as User | null,
    isMoving: false,
    showSubroomArea: false,
    isDebugMode: false,
    providers: [] as LoginProvider[],
    version: 'vxxxx',
    isFocus: false as boolean,
    isShowEditor: false as boolean,
    defaultEditingMessage: '',
    shouldSendSelectedMessage: true as boolean,
    boardType: '' as any,
    isCapturePictureInPicture: false as boolean,
    pipMode: '' as string,
    siteStatus: undefined as SiteStatus | undefined,
    campaigns: [] as Campaign[],
    isRoomObjectHover: false as boolean,
    isRoomObjectMenuHover: false as boolean,
    invitation: undefined as InvitedSpaceIdAndRoomId | undefined,
  },
  mutations: {
    ToggleDebugMode(state) {
      state.isDebugMode = !state.isDebugMode;
    },
    SetIsIPad(state, isIPad: boolean) {
      state.isIPad = isIPad;
    },
    SetUser(state, user: User) {
      state.user = user;
    },
    SetUserProperties(state, properties: UserProperties) {
      state.userProperties = properties;
    },
    SetRoomScale(state, scale: number) {
      state.roomScale = scale;
    },
    SetIsVideoAvailable(state, isVideoAvailable: boolean) {
      state.isVideoAvailable = isVideoAvailable;
    },
    SetIsAudioAvailable(state, isAudioAvailable: boolean) {
      state.isAudioAvailable = isAudioAvailable;
    },
    SetIsVideoEnableOnInit(state, isVideoEnabledOnInit: boolean) {
      state.isVideoEnabledOnInit = isVideoEnabledOnInit;
    },
    SetIsAudioEnableOnInit(state, isAudioEnabledOnInit: boolean) {
      state.isAudioEnabledOnInit = isAudioEnabledOnInit;
    },
    SetUploadedImageBase64(state, image: ImageWithDimensions | null) {
      state.uploadedImageBase64 = image;
    },
    SetScreenSharing(state, isScreenSharing: boolean) {
      state.isScreenSharing = isScreenSharing;
    },
    SetSiteInfo(state, config: SiteInfo) {
      state.siteInfo = config;
    },
    SetDataService(state, dataService: DataService) {
      state.dataService = dataService;
    },
    SetSettings(state, gs: Partial<GeneralSettingsSnapshot>) {
      const { dataService, user } = state;
      if (!dataService || !user) {
        return;
      }
      const { settings, properties } = gs;
      if (settings) {
        // device id 関連はPCごとに異なるのでローカルで保存するのみ
        state.videoDeviceId = settings.selectedVideoId;
        state.audioInputDeviceId = settings.selectedAudioId;
        state.audioOutputDeviceId = settings.selectedOutputId;

        store.set('chosenVideoInputDeviceId', settings.selectedVideoId);
        store.set('chosenAudioOutputDeviceId', settings.selectedOutputId);
        store.set('chosenAudioInputDeviceId', settings.selectedAudioId);

        if (user.avatarBase64 !== (settings.avatarBase64 ?? '') || user.avatarShape !== settings.avatarShape) {
          if (settings.avatarBase64) {
            dataService.emitSetAvatar(settings.avatarBase64, settings.avatarShape || 'circle');
          } else {
            dataService.emitRemoveAvatar();
          }
        }
        if (user.displayName !== settings.displayName) {
          dataService.emitDisplayName(settings.displayName);
        }
        if (user.voiceAreaColor !== settings.voiceAreaColor) {
          dataService.emitVoiceAreaColor(settings.voiceAreaColor);
        }
      }
      if (properties) {
        const keys: (keyof UserProperties)[] = [
          // 設定画面
          'selectedCustomModes',
          'selectedNotificationMode',
          'selectedProximityNotificationMode',
          'selectedMessageNotificationMode',
          'enableNoiseSuppressor',
          'enableShortcut',
          // 入室前 or 入室後
          // 'videoEnableOnInit',
          // 'audioEnableOnInit',
          // 入室時
          'lastSelectedMenuCategory',
          'lastSelectedSpaceId',
          'lastSelectedRoomId',
          // 入室後
          'lastSelectedRoomInfoTab',
          'lastSelectedRoomHistoryFilters',
        ];
        const changedKeys = keys.filter(
          (k) => typeof properties[k] !== 'undefined' && !isSameValue(state.userProperties, properties, k),
        );
        if (changedKeys.length === 0) {
          // 更新がないので処理しない
          return;
        }
        const saveData: Partial<UserProperties> = { userId: user.id };
        changedKeys.forEach((k) => (saveData[k] = properties[k] as any));
        dataService.emitUpdateUserProperties(saveData);
      }
    },
    SetProviders(state, providers: LoginProvider[]) {
      state.providers = providers;
    },
    SetHoverUser(state, user: User) {
      state.hoverUser = user;
    },
    SetIsMoving(state, isMoving: boolean) {
      state.isMoving = isMoving;
    },
    SetShowSubroomArea(state, showSubroomArea: boolean) {
      state.showSubroomArea = showSubroomArea;
    },
    setVersion(state, version: string) {
      state.version = version;
    },
    setIsFocus(state, isFocus: boolean) {
      state.isFocus = isFocus;
    },
    SetIsShowEditor(state, isShowEditor: boolean) {
      state.isShowEditor = isShowEditor;
    },
    SetDefaultEditingMessage(state, defaultEditingMessage: string) {
      state.defaultEditingMessage = defaultEditingMessage;
    },
    SetShouldSendSelectedMessage(state, shouldSendSelectedMessage: boolean) {
      state.shouldSendSelectedMessage = shouldSendSelectedMessage;
    },
    setBoardType(state, boardType: any) {
      state.boardType = boardType;
    },
    setIsCapturePictureInPicture(state, isCapturePictureInPicture: boolean) {
      state.isCapturePictureInPicture = isCapturePictureInPicture;
    },
    setPipMode(state, pipMode: string) {
      state.pipMode = pipMode;
    },
    setSiteStatus(state, status: SiteStatus) {
      state.siteStatus = status;
    },
    setCampaigns(state, campaigns: Campaign[]) {
      state.campaigns = campaigns;
    },
    setCampaign(state, campaign: Campaign) {
      const index = state.campaigns.findIndex((c) => c.id === campaign.id);
      if (index >= 0) {
        state.campaigns[index] = campaign;
      }
    },
    addCampaign(state, campaign: Campaign) {
      state.campaigns.push(campaign);
    },
    deleteCampaigns(state, campaigns: Campaign[]) {
      const ids = campaigns.map((c) => c.id);
      state.campaigns = state.campaigns.filter((c) => !ids.includes(c.id));
    },
    setIsRoomObjectHover(state, isRoomObjectHover: boolean) {
      state.isRoomObjectHover = isRoomObjectHover;
    },
    setIsRoomObjectMenuHover(state, isRoomObjectMenuHover: boolean) {
      state.isRoomObjectMenuHover = isRoomObjectMenuHover;
    },
    setInvitation(state, invitedRoomIdAndSpaceId: InvitedSpaceIdAndRoomId | undefined) {
      state.invitation = invitedRoomIdAndSpaceId;
    },
  },
  getters: {
    isTrialSite(state) {
      return state.siteInfo?.isDemo;
    },
    isTrialGuestUser(state) {
      return state.user?.authType === 'guest' && state.siteInfo?.isDemo;
    },
    isTrialZawaroomAccountUser(state) {
      return state.user?.authType !== 'guest' && state.siteInfo?.isDemo;
    },
    isAdminUser(state) {
      return !!state.user?.roles?.find((r) => r.id === 'Admin');
    },
  },
  actions: {},
  modules: {},
});
function isSameValue(current: any, next: any, key: string): boolean {
  const currentVal = current[key];
  const nextVal = next[key];
  if (Array.isArray(currentVal)) {
    if (Array.isArray(nextVal) && currentVal.length === nextVal.length) {
      nextVal.sort();
      currentVal.sort();
      // 同じindexに異なるものが入ってないことをチェック
      return !currentVal.find((v, i) => v !== nextVal[i]);
    }
    return false;
  }
  return currentVal === nextVal;
}
